/*@flow*/
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useEffect, Fragment } from 'react';
import list from 'lib/proforma/templates/List';
import initFilePicker from 'lib/initFilePicker';
import NonEmptyArray from 'lib/NonEmptyArray';
import cx from 'classnames';
import useUploadedFiles from 'lib/useUploadedFiles';
import Blank from 'lib/Blank';

const buttonCss = css(`
  font-size: 1.8rem;
  padding: 1.5rem 2rem;
  display: inline-block;
  margin: 0;
  background: var(--info);
  color: white !important;
  min-width: 108px;
`);

function AddButton({ onChange, value, onClick }) {
  const accepted = ['image/*'];
  const [items, isLoading, setLoading] = useUploadedFiles(value, accepted);

  useEffect(() => {
    if (Blank.is(items)) return;
    // Add a key manually, since we disabled the default add button.
    onClick();
    const urls = items.map((v) => v.url);
    const newUrls = (value || [])
      .filter((v) => !urls.includes(v))
      .concat(urls)
      .filter(Boolean);
    onChange(newUrls);
  }, [items]);

  return (
    <button
      type="button"
      onClick={() => setLoading(true)}
      disabled={isLoading}
      css={buttonCss}
      className="text-left d-block"
      data-testid="AddButton"
    >
      <i
        className={cx('fa', {
          'fa-file-upload': !isLoading,
          'fa-spinner fa-spin': isLoading,
        })}
      />{' '}
      Upload
    </button>
  );
}

function Item({ removeBtn, value, url, onChange, index }) {
  const cropFiles = (files) => {
    const fileUrls = files.map((v) => v.url);

    initFilePicker().then((client) => {
      client
        .crop(files.map((v) => v.url))
        .then((res) => res.map((v) => v.url))
        .then((urls) => {
          if (value) {
            const u = value.filter((v) => !fileUrls.includes(v)).concat(urls);
            onChange(u);
          }
        });
    });
  };

  function onEditImage(e) {
    e.preventDefault();
    cropFiles([{ url }]);
  }

  return (
    <div
      data-testid={`RowItem-${index}`}
      className="col-sm-3 col-lg-3 photo p-0"
    >
      <a className="d-block" onClick={onEditImage}>
        <img className="w-100" src={url} />
      </a>
      {removeBtn && (
        <a
          data-testid={`RemoveBtn-${index}`}
          className="btn btn-block btn-link mt-1"
          onClick={(e) => {
            removeBtn.click(e);
          }}
        >
          {removeBtn.label}
        </a>
      )}
    </div>
  );
}

const PhotoUploaderTemplate: any = list.clone({
  Fieldset(locals) {
    const { disabled, label, value, onChange, add, help, rows } = locals;

    return (
      <div
        disabled={disabled}
        className="form-group form-group-depth-2 form-group-photoUrls mb-3"
      >
        {label && (
          <label data-testid="FieldLabel" className="col-form-label">
            {label}
          </label>
        )}
        {add && (
          <AddButton
            onChange={onChange}
            onClick={() => add.click()}
            value={value}
          />
        )}
        {help && (
          <div data-testid="HelpText" className="text-muted small">
            {help}
          </div>
        )}
        {NonEmptyArray.is(rows) && (
          <div data-testid="RowContainer" className="mt-3">
            {rows}
          </div>
        )}
      </div>
    );
  },
  Row({ row, value, onChange }) {
    const removeBtn = row.buttons.find((v) => v.type === 'remove');
    type RowItemT = ?string;
    const url: RowItemT = ((row: any).value: any);

    if (!url) return null;

    return (
      <Fragment>
        <div data-testid={`RowItemMeta-${row.index}`} className="hide">
          {row.input}
        </div>
        <Item
          removeBtn={removeBtn}
          value={value}
          index={row.index}
          url={url}
          onChange={onChange}
        />
      </Fragment>
    );
  },
});

export default PhotoUploaderTemplate;
